import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {formatEventContainerLocation, getEventContainerGeographyLocationColumn} from "~/utilities/eventContainerLocationDisplay";
/**
 * Generic card display helpers for the config harness (no workspace packages).
 */

import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {getPathFromLeaf} from "~/utilities/accessControl/ouHierarchyDisplay";
import {resolveDesignTokenByName} from "~/utilities/resolveDesignTokenByName";
import {flatDispositionToDisplayNameFormat} from "~/utilities/utilities";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getOptionalEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

/**
 * Resolve a config color that may be a design-token name or a raw CSS color.
 * Accepts `#RRGGBB`, `rgb(...)`, `rgba(...)`, `var(...)`, `hsl(...)`, or `color.*` tokens.
 */
export function resolveConfigColor(value: string | null | undefined, fallback: string): string {
    if (value === null || value === undefined) {
        return fallback;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
        return fallback;
    }
    const lower = trimmed.toLowerCase();
    if (
        trimmed.startsWith("#") ||
        lower.startsWith("var(") ||
        lower.startsWith("rgb(") ||
        lower.startsWith("rgba(") ||
        lower.startsWith("hsl(") ||
        lower.startsWith("hsla(")
    ) {
        return trimmed;
    }
    const fromToken = resolveDesignTokenByName(trimmed);
    return fromToken === "" ? trimmed : fromToken;
}

/** Format a stored contact number for display (e.g. +91…). */
export function formatContactNumberDisplay(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed === "") {
        return trimmed;
    }
    if (trimmed.startsWith("+91") === true) {
        return trimmed;
    }
    const digitsOnly = trimmed.replace(/\D/g, "");
    if (digitsOnly.length === 12 && digitsOnly.startsWith("91") === true) {
        return `+91${digitsOnly.slice(2)}`;
    }
    if (digitsOnly.length === 11 && digitsOnly.startsWith("0") === true) {
        return `+91${digitsOnly.slice(1)}`;
    }
    if (digitsOnly.length === 10) {
        return `+91${digitsOnly}`;
    }
    return trimmed;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Join L1–L4 disposition path labels from raw level strings. */
export function listDispositionPathChips(
    levels: ReadonlyArray<string>,
): Array<{slug: string; label: string}> {
    return levels
        .map((slug) => slug.trim())
        .filter((slug) => slug !== "")
        .map((slug) => ({
            slug: slug,
            label: flatDispositionToDisplayNameFormat(slug),
        }));
}

export function formatDispositionPathFromLevels(
    levels: ReadonlyArray<string>,
    separator: string,
): string {
    const trimmedLevels = levels.map((level) => level.trim()).filter((level) => level !== "");
    // Stored L1–L4 fields often all hold the journey disposition UUID. Title-casing
    // those ids looks like a UUID on the card — skip and let the resolver/catalog run.
    if (trimmedLevels.some((level) => UUID_PATTERN.test(level) === true)) {
        return "";
    }
    return listDispositionPathChips(levels)
        .map((chip) => chip.label)
        .join(separator);
}

export function getJourneyAssignedOuColumnLabel(
    journey: OttopilotEventContainer,
    template: EventContainerTemplate,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    getAdditionalColumns: CapabilityUdoColumnResolver,
): string {
    if (template.configuration.organizationalUnitColumnConfig === null) {
        return getEventContainerGeographyLocationColumn(getAdditionalColumns, template).displayName;
    }
    const organizationalUnitId = getOptionalEventContainerOrganizationalUnitId(journey, template);
    if (organizationalUnitId === null) {
        return organizationalUnitTemplates[0]?.displayName ?? "OU";
    }
    const path = getPathFromLeaf(
        organizationalUnitId,
        [...organizationalUnits],
        [...organizationalUnitTemplates],
    );
    const leafEntry = path[path.length - 1];
    if (leafEntry !== undefined && leafEntry.type.trim() !== "") {
        return leafEntry.type;
    }
    return organizationalUnitTemplates[0]?.displayName ?? "OU";
}

export function getJourneyAssignedOuName(
    journey: OttopilotEventContainer,
    template: EventContainerTemplate,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    getAdditionalColumns: CapabilityUdoColumnResolver,
): string {
    if (template.configuration.organizationalUnitColumnConfig === null) {
        return formatEventContainerLocation(getAdditionalColumns, journey, template, () => {
            throw new Error("A geography location cannot be formatted as an OU");
        });
    }
    const organizationalUnitId = getOptionalEventContainerOrganizationalUnitId(journey, template);
    if (organizationalUnitId === null) {
        return "-";
    }
    const path = getPathFromLeaf(
        organizationalUnitId,
        [...organizationalUnits],
        [...organizationalUnitTemplates],
    );
    const leafEntry = path[path.length - 1];
    if (leafEntry !== undefined && leafEntry.name.trim() !== "") {
        return leafEntry.name;
    }
    return "-";
}
